import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

function extractNumber(text) {
  if (!text) return "";
  const t = String(text).trim();
  const m = t.match(/verify\/([^/?#]+)/i);
  if (m) return decodeURIComponent(m[1]);
  return t;
}

export default function GatePassVerify() {
  const { number: paramNumber } = useParams();
  const { can } = useAuth();
  const [number, setNumber] = useState(paramNumber || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const verify = async (num) => {
    const n = (num ?? number).trim();
    if (!n) { toast.error("Enter a gate pass number"); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.get(`/gate-passes/verify/${encodeURIComponent(n)}`);
      setResult(r.data);
    } catch { toast.error("Verification failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (paramNumber) verify(paramNumber); }, [paramNumber]); // eslint-disable-line

  const stopScan = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch (e) { console.debug("QR scanner stop/clear:", e); }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScan = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const h = new Html5Qrcode("gp-qr-reader");
        scannerRef.current = h;
        await h.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
          async (decoded) => {
            const n = extractNumber(decoded);
            setNumber(n);
            await stopScan();
            verify(n);
          }, () => {});
      } catch (e) {
        toast.error("Could not access camera. Enter the number manually.");
        setScanning(false);
      }
    }, 100);
  };

  useEffect(() => () => { stopScan(); }, []); // eslint-disable-line

  if (!can("gate_pass", "view"))
    return (
      <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
        <Icons.Lock className="h-8 w-8 mx-auto text-destructive mb-3" />
        <h2 className="font-display text-xl font-bold">Access Restricted</h2>
        <p className="text-muted-foreground text-sm mt-2">You don't have permission to verify gate passes.</p>
      </div>
    );

  const genuine = result?.genuine;

  return (
    <div className="space-y-6 max-w-xl mx-auto">
      <div className="text-center">
        <div className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: "#1e3a5f1a", color: "#1e3a5f" }}>
          <Icons.QrCode className="h-7 w-7" />
        </div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Gatekeeper Verify</h1>
        <p className="text-sm text-muted-foreground mt-1">Scan the QR on the gate pass or enter its number to confirm it's genuine.</p>
      </div>

      <Card className="p-5 space-y-4">
        <div className="flex gap-2">
          <Input value={number} onChange={(e) => setNumber(e.target.value.toUpperCase())} placeholder="GPI-BSP-YYYYMMDD-0001"
            onKeyDown={(e) => { if (e.key === "Enter") verify(); }} data-testid="verify-number-input" />
          <Button onClick={() => verify()} disabled={loading} data-testid="verify-btn">
            {loading ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Search className="h-4 w-4" />}
            <span className="ml-1.5 hidden sm:inline">Verify</span>
          </Button>
        </div>
        {!scanning ? (
          <Button variant="outline" className="w-full gap-2" onClick={startScan} data-testid="verify-scan-btn">
            <Icons.Camera className="h-4 w-4" />Scan QR with Camera
          </Button>
        ) : (
          <div className="space-y-2">
            <div id="gp-qr-reader" className="rounded-lg overflow-hidden border border-border" />
            <Button variant="outline" className="w-full gap-2" onClick={stopScan} data-testid="verify-stop-btn">
              <Icons.X className="h-4 w-4" />Stop Camera
            </Button>
          </div>
        )}
      </Card>

      {result && (
        <Card className={`p-5 border-2 ${genuine ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-red-500 bg-red-50 dark:bg-red-950/30"}`} data-testid="verify-result">
          <div className="flex items-center gap-3 mb-3">
            {genuine ? <Icons.ShieldCheck className="h-8 w-8 text-emerald-600" /> : <Icons.ShieldAlert className="h-8 w-8 text-red-600" />}
            <div>
              <div className={`font-display text-xl font-bold ${genuine ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`} data-testid="verify-verdict">
                {genuine ? "GENUINE — Issued" : !result.found ? "NOT FOUND" : result.status === "cancelled" ? "CANCELLED" : "NOT ISSUED"}
              </div>
              <div className="text-sm text-muted-foreground">{result.gate_pass_number || number}</div>
            </div>
          </div>
          {result.found ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="overline text-muted-foreground">Party</div>{result.party_name || "—"}</div>
              <div><div className="overline text-muted-foreground">Vehicle</div>{result.vehicle_number || "—"}</div>
              <div><div className="overline text-muted-foreground">Movement</div>{result.movement_type || "—"}</div>
              <div><div className="overline text-muted-foreground">Purpose</div>{result.purpose || "—"}</div>
              <div><div className="overline text-muted-foreground">Driver</div>{result.driver_name || "—"}</div>
              <div><div className="overline text-muted-foreground">Issued</div>{result.issue_date ? `${result.issue_date} ${result.issue_time || ""}` : "—"}</div>
              <div><div className="overline text-muted-foreground">Authorized By</div>{result.authorized_by_name || "—"}</div>
              <div><div className="overline text-muted-foreground">Materials</div>{result.materials_count} item(s)</div>
              {result.status === "cancelled" && <div className="col-span-2 text-red-600"><div className="overline text-muted-foreground">Cancel Reason</div>{result.cancel_reason || "—"}</div>}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{result.message}</p>
          )}
        </Card>
      )}
    </div>
  );
}
